import type { AppManifest, CustomActionConfig } from '@wix/platform-editor-sdk';
import type { TranslationFunction } from 'i18next';

import { shouldAddManageMemberAccessAction } from '../../../../utils/experiments';
import { getIsEditor3 } from '../../../util';
import { AppManagerAction } from '../../types';

type CustomAppActions = NonNullable<AppManifest['appDescriptor']>['customActions'];
type MainAppActions = NonNullable<AppManifest['appDescriptor']>['mainActions'];

const getMainAppActions = (t: TranslationFunction): MainAppActions => [
  {
    actionId: AppManagerAction.OpenCustomizeMemberPage,
    icon: 'appManager_settingsAction',
    title: t('Apps_Manager_Customize_Settings_Page'),
  },
];

const getCustomAppActions = async (t: TranslationFunction): Promise<CustomAppActions> => [
  {
    actionId: AppManagerAction.OpenMembersAccountBmDashboard,
    type: 'dashboard',
    icon: 'appManager_settingsAction',
    title: t('Apps_Manager_Customize_Members_Profiles'),
  },
  {
    actionId: AppManagerAction.OpenBadgesDashboard,
    type: 'dashboard',
    icon: 'addAction',
    title: t('Apps_Manager_Open_Badges_Page'),
  },
  {
    actionId: AppManagerAction.OpenMembersAddPanel,
    type: 'editorActions',
    icon: 'appManager_addElementsAction',
    title: t('Apps_Manager_Open_Members_Add_Panel'),
  },
  {
    actionId: AppManagerAction.OpenMembersAreaPagesPanel,
    type: 'editorActions',
    icon: 'appManager_pagesAction',
    title: t('Apps_Manager_Manage_Member_Pages'),
  },
  {
    actionId: AppManagerAction.OpenSiteMembersDashboard,
    type: 'dashboard',
    icon: 'appManager_memberAction',
    title: t('Apps_Manager_Engage_With_Your_Members'),
  },
  ...((await shouldAddManageMemberAccessAction())
    ? [
        {
          actionId: AppManagerAction.OpenSiteMembersAccessDashboard,
          type: 'dashboard',
          icon: 'passkey',
          title: t('Apps_Manager_Manage_Member_Access'),
        } as CustomActionConfig,
      ]
    : []),
];

const getEditor3CustomAppActions = (t: TranslationFunction): CustomAppActions => [
  {
    actionId: AppManagerAction.OpenManageMemberPagesPanel,
    type: 'editorActions',
    icon: 'appManager_pagesAction',
    title: t('Apps_Manager_Manage_Member_Pages'),
  },
  {
    actionId: AppManagerAction.OpenMembersAccountBmDashboard,
    type: 'dashboard',
    icon: 'appManager_settingsAction',
    title: t('Apps_Manager_Customize_Fields'),
  },
  {
    actionId: AppManagerAction.OpenSiteMembersDashboard,
    type: 'dashboard',
    icon: 'users',
    title: t('Apps_Manager_Engage_With_Your_Members'),
  },
  {
    actionId: AppManagerAction.OpenSiteMembersAccessDashboard,
    type: 'dashboard',
    icon: 'passkey',
    title: t('Apps_Manager_Manage_Member_Access_Editor3'),
  },
  {
    actionId: AppManagerAction.OpenBadgesDashboard,
    type: 'dashboard',
    icon: 'addAction',
    title: t('Apps_Manager_Open_Badges_Page'),
  },
];

export const getAppDescriptor = async (t: TranslationFunction): Promise<AppManifest['appDescriptor']> => {
  const isEditor3 = getIsEditor3();
  return {
    mainActions: isEditor3 ? [] : getMainAppActions(t),
    defaultActions: { learnMoreKB: 'b74bcbba-91c8-4951-a3de-a1dc698f6356' },
    customActions: isEditor3 ? getEditor3CustomAppActions(t) : await getCustomAppActions(t),
  };
};
